import { useStore } from '@nanostores/react';
import { ArrowRightToLine, Bug } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { analytics, logger } from '@/shared/lib';
import { useCarePatientViewersQuery } from '@/entities/care-patients';
import { useMyStatusQuery } from '@/entities/me';
import { Button } from '@/shared/ui/shadcn/button';
import {
  $copilotCollapsed,
  isCopilotTabId,
  openCopilotOverlay,
  setCopilotActiveTab,
  toggleCopilotPanel,
} from '@/shared/stores/ui';
import { clearDebugEvents, emitDebugEvent } from '@/features/copilot-debug';
import { useCopilotChat, useCopilotThreads } from '@/features/copilot/lib';
import { useCopilotToolPartRenderers } from '@/features/copilot/tooling/useCopilotToolPartRenderers';
import { CopilotToolUiProvider } from '@/features/copilot/ui/ToolUi/CopilotToolUiProvider';
import { ThreadPopover } from '@/features/copilot/ui/ThreadPopover';
import { useProposedActionsPlan } from '@/features/proposed-actions-plan';

import { CopilotContentOutlet } from './CopilotContentOutlet';
import { CopilotDock } from './CopilotDock';
import { CopilotHeader } from './CopilotHeader';
import { useCopilotPanelUiState } from './useCopilotPanelUiState';

interface CopilotPanelProps {
  userId: string;
}

/**
 * CopilotPanel — the right panel widget for AI copilot interaction.
 *
 * Composes:
 * - CopilotHeader: navigation between copilot surfaces + collapse toggle
 * - CopilotContentOutlet: scrollable body for active view
 * - MessageComposer: pinned input area (always visible)
 *
 * @remarks
 * This is a composition-heavy widget. It coordinates UI surfaces but
 * does not contain domain/business logic. AI interactions and data
 * will be handled by features/entities.
 *
 * Tab state is managed by the shared $copilotActiveTab nanostore.
 *
 * @example
 * ```tsx
 * <CopilotPanel />
 * ```
 */
export function CopilotPanel({ userId }: CopilotPanelProps) {
  const { activeTab, effectiveComposerMode } = useCopilotPanelUiState();
  const isCollapsed = useStore($copilotCollapsed);
  const { isEmpty: isProposedActionsEmpty } = useProposedActionsPlan();
  const { data: myStatus } = useMyStatusQuery();
  const canOpenAiDebug = myStatus?.subsystemAccess?.['evaluation_admin'] === true;
  const viewersQuery = useCarePatientViewersQuery(userId);
  const viewers = useMemo(
    () => viewersQuery.data?.viewers.map((viewer) => ({ name: viewer.display_name })) ?? [],
    [viewersQuery.data?.viewers]
  );

  useEffect(() => {
    clearDebugEvents();
  }, [userId]);

  const threadManager = useCopilotThreads({ userId });

  const { messages, sendMessage, stop, status, addToolOutput, setMessages } = useCopilotChat({
    userId,
    threadId: threadManager.activeThreadId,
  });
  const isBusy = status === 'submitted' || status === 'streaming';
  const showPlaceholderContent =
    activeTab === 'copilot' && messages.length === 0 && status === 'ready';
  const toolPartRenderers = useCopilotToolPartRenderers();

  const saveThreadRef = useRef(threadManager.saveThread);
  useEffect(() => {
    saveThreadRef.current = threadManager.saveThread;
  }, [threadManager.saveThread]);

  const activeThreadId = threadManager.activeThreadId;
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    if (messages.length === 0 || isBusy) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      void saveThreadRef.current(activeThreadId, messages);
    }, 2000);
    return () => clearTimeout(saveTimerRef.current);
  }, [messages, isBusy, activeThreadId]);

  const handleSwitchThread = useCallback(
    (threadId: string) => {
      stop();
      void threadManager.switchThread(threadId, messages, setMessages);
    },
    [stop, threadManager, messages, setMessages]
  );

  const handleNewThread = useCallback(() => {
    stop();
    void threadManager.createNewThread(messages, setMessages);
  }, [stop, threadManager, messages, setMessages]);

  const handleSendCopilotMessage = (text: string) => {
    logger.info('copilot.chat.userMessage.send', {
      userId,
      textLength: text.length,
    });
    void analytics.track({
      name: 'copilot.chat.userMessage.send',
      context: { actor: 'clinician' },
      properties: { userId, textLength: text.length },
    });
    emitDebugEvent('ai.user.messageSent', 'User message sent', { userId, textLength: text.length });
    sendMessage({ text });
  };

  if (isCollapsed) {
    return (
      <div className="flex h-full items-start justify-center pt-2">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={toggleCopilotPanel}
          aria-label="Expand panel"
        >
          <ArrowRightToLine className="h-4 w-4 rotate-180" />
        </Button>
      </div>
    );
  }

  return (
    <CopilotToolUiProvider
      toolPartRenderers={toolPartRenderers}
      addToolOutput={addToolOutput}
      onSendMessage={handleSendCopilotMessage}
    >
      <div className="relative flex h-full flex-col">
        {/* Header: tab navigation */}
        <div className="shrink-0">
          <CopilotHeader
            activeTab={activeTab}
            onTabChange={(tabId) => {
              if (!isCopilotTabId(tabId)) return;
              setCopilotActiveTab(tabId);
            }}
            isCollapsed={isCollapsed}
            viewers={viewers}
            onToggleCollapsed={toggleCopilotPanel}
            extraActions={
              <>
                {canOpenAiDebug ? (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => openCopilotOverlay('aiDebug')}
                    aria-label="Open AI debug panel"
                  >
                    <Bug className="h-4 w-4" />
                  </Button>
                ) : null}
                <ThreadPopover
                  threads={threadManager.threads}
                  activeThreadId={threadManager.activeThreadId}
                  isLoading={threadManager.isLoading}
                  onSwitchThread={handleSwitchThread}
                  onNewThread={handleNewThread}
                />
              </>
            }
          />
        </div>

        {/* Body: content area — scrolling is managed by StickToBottom inside CopilotThread */}
        <div className="relative flex-1 overflow-hidden">
          <CopilotContentOutlet activeTab={activeTab} messages={messages} status={status} />
        </div>

        {/* Composer: floating overlay pinned at bottom */}
        <CopilotDock
          userId={userId}
          effectiveComposerMode={effectiveComposerMode}
          isBusy={isBusy}
          showQuickActions={showPlaceholderContent && isProposedActionsEmpty}
          onStop={() => stop()}
          onSendMessage={handleSendCopilotMessage}
        />
      </div>
    </CopilotToolUiProvider>
  );
}
